import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useToast } from "../../hooks/use-toast";
import { formatDate, getPaymentStructureLabel, calculateVATInclusiveAmount } from "../../lib/appUtils";
import { ClientPaymentChoice } from "../shared/ClientPaymentChoice";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Toaster } from "../ui/toaster";
import { 
  FileText, Shield, Award, PiggyBank, XCircle, ArrowRight, 
  CreditCard, CheckCircle, Zap 
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmbedBusiness = () => {
  const { businessId } = useParams();
  const [businessData, setBusinessData] = useState(null);
  const [currentStep, setCurrentStep] = useState('enter_code'); // 'enter_code', 'payment_choice', 'signature', 'checkout'
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedPaymentOption, setSelectedPaymentOption] = useState(null);
  const [projectReference, setProjectReference] = useState('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [signatureData, setSignatureData] = useState({
    full_name: '',
    email: '',
    position: '',
    company: '',
    agreed: false,
    identity_confirmed: false
  });
  const [showContract, setShowContract] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchBusinessData();
    
    // Check URL for direct project access
    const urlParams = new URLSearchParams(window.location.search);
    const projectSlug = urlParams.get('project');
    const paymentRef = urlParams.get('ref');
    
    if (projectSlug || paymentRef) {
      setProjectReference(projectSlug || paymentRef);
      handleDirectAccess(projectSlug || paymentRef);
    }
  }, [businessId]);
  // Update signature data when selectedProject becomes available
  useEffect(() => {
    if (selectedProject) {
      setSignatureData(prev => ({
        ...prev,
        full_name: selectedProject.client_name || '',
        email: selectedProject.client_email || '',
        company: selectedProject.client_company || ''
      }));
    }
  }, [selectedProject]);

  const fetchBusinessData = async () => {
    try {
      const response = await axios.get(`${API}/embed/all-projects/${businessId}`);
      setBusinessData(response.data);
    } catch (error) {
      console.error('Failed to load business data:', error);
      setBusinessData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDirectAccess = async (reference) => {
    setVerifying(true);
    try {
      // Try to find project by reference
      const response = await axios.get(`${API}/checkout/${reference}`);
      if (response.data.project) {
        setSelectedProject(response.data.project);
        setCurrentStep('payment_choice');
      }
    } catch (error) {
      setCurrentStep('enter_code');
    } finally {
      setVerifying(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    if (!projectReference.trim()) return;

    setVerifying(true);
    try {
      const response = await axios.get(`${API}/checkout/${projectReference.trim()}`);
      if (response.data.project) {
        setSelectedProject(response.data.project);
        setCurrentStep('payment_choice');
      } else {
        alert('Invalid payment reference. Please check and try again.');
      }
    } catch (error) {
      alert('Invalid payment reference. Please check and try again.');
    } finally {
      setVerifying(false);
    }
  };

  // Function moved to ClientPaymentChoice component

  const handlePaymentChoiceComplete = () => {
    setCurrentStep('checkout');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#e6007e] mx-auto"></div>
          <p className="mt-4 text-gray-600 font-body text-sm">Loading secure checkout...</p>
        </div>
      </div>
    );
  }

  if (!businessData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow-sm p-8 border border-gray-200">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-gray-900 mb-2 font-heading">Checkout Not Available</h2>
            <p className="text-gray-600 font-body text-sm">
              This payment portal is currently unavailable.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { business } = businessData;

  // Step 1: Enter Payment Code/Reference
  if (currentStep === 'enter_code') {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 p-4">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            <div className="flex items-center space-x-3">
              {business.logo_url && (
                <img 
                  src={business.logo_url} 
                  alt={business.name}
                  className="w-8 h-8 object-contain"
                />
              )}
              <div>
                <h1 className="text-lg font-bold text-gray-900 font-heading">{business.name}</h1>
                <p className="text-sm text-gray-600 font-body">Payment Portal</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 text-gray-500">
              <Shield className="w-4 h-4 text-green-600" />
              <span className="text-sm font-body">Secured by Paify</span>
            </div>
          </div>
        </div>

        {/* Payment Reference Form */}
        <div className="flex items-center justify-center py-20 px-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 w-full max-w-md">
            <div className="text-center mb-6">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2 font-heading">Enter Payment Reference</h2>
              <p className="text-gray-600 font-body text-sm">
                Please enter your project reference or payment link to continue.
              </p>
            </div>
            
            <form onSubmit={handleCodeSubmit} className="space-y-4">
              <div>
                <Label htmlFor="project_reference" className="font-body">Payment Reference</Label>
                <Input
                  id="project_reference"
                  value={projectReference}
                  onChange={(e) => setProjectReference(e.target.value)}
                  placeholder="Enter project ID or reference"
                  className="font-body text-center"
                  required
                />
                <p className="text-xs text-gray-500 mt-1 font-body text-center">
                  Check your email or quote for payment reference
                </p>
              </div>
              
              <Button
                type="submit"
                disabled={verifying || !projectReference.trim()}
                className="w-full bg-[#e6007e] hover:bg-[#c4006b] text-white font-body"
              >
                {verifying ? "Verifying..." : "Continue to Payment"}
              </Button>
            </form>

            {/* Trust Features */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-center space-x-6 text-xs text-gray-500 font-body">
                <div className="flex items-center space-x-1">
                  <Shield className="w-3 h-3 text-green-600" />
                  <span>SSL Secure</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Award className="w-3 h-3 text-blue-600" />
                  <span>Trust Badge</span>
                </div>
                <div className="flex items-center space-x-1">
                  <PiggyBank className="w-3 h-3 text-green-600" />
                  <span>VAT Vault</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Use ACTUAL ClientPaymentChoice Component (EXACT same as link version)
  if (currentStep === 'payment_choice' && selectedProject) {
    return (
      <ClientPaymentChoice 
        project={selectedProject}
        provider={{ business: businessData?.business }}
        slugProp={projectReference}
        onChoiceComplete={() => {
          // After signing, go to checkout
          window.location.href = `/checkout/${projectReference}`;
        }}
        onRefreshData={fetchBusinessData}
      />
    );
  }

  // No need for separate steps - ClientPaymentChoice handles payment selection, contract review, and signature

  // Default fallback
  return null;
};

const EmbeddedProjectCheckout = ({ project, business, onBack }) => {
  const [projectData, setProjectData] = useState(null);
  const [contractSigned, setContractSigned] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [showFullContract, setShowFullContract] = useState(false);
  const [showFullContractModal, setShowFullContractModal] = useState(false);
  const [contractContent, setContractContent] = useState('');
  const [signatureData, setSignatureData] = useState({
    full_name: '',
    email: '',
    position: '',
    company: '',
    agreed: false,
    identity_confirmed: false
  });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchProjectData();
  }, [project]);
  // Update signature data when project becomes available
  useEffect(() => {
    if (project) {
      setSignatureData(prev => ({
        ...prev,
        full_name: project.client_name || '',
        email: project.client_email || '',
        company: project.client_company || ''
      }));
    }
  }, [project]);

  const fetchProjectData = async () => {
    try {
      const response = await axios.get(`${API}/checkout/${project.slug}`);
      setProjectData(response.data);
      setContractSigned(response.data.project.contract_agreed);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load project details",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignContract = async (e) => {
    e.preventDefault();
    if (!signatureData.agreed || !signatureData.full_name.trim()) {
      toast({
        title: "Agreement Required",
        description: "Please enter your full name and agree to the contract terms",
        variant: "destructive"
      });
      return;
    }

    setProcessing(true);
    try {
      await axios.post(`${API}/checkout/${project.slug}/sign`, {
        full_name: signatureData.full_name.trim(),
        agreed: signatureData.agreed,
        ip_address: '127.0.0.1',
        user_agent: navigator.userAgent
      });

      setContractSigned(true);
      toast({
        title: "Contract Signed",
        description: "Thank you! You can now proceed with payment."
      });
      
      fetchProjectData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to sign contract",
        variant: "destructive"
      });
    } finally {
      setProcessing(false);
    }
  };

  const handlePayment = async (milestoneId) => {
    if (!selectedPaymentMethod) {
      toast({
        title: "Payment Method Required",
        description: "Please select a payment method",
        variant: "destructive"
      });
      return;
    }

    setProcessing(true);
    try {
      await axios.post(`${API}/checkout/${project.slug}/pay/${milestoneId}`);
      
      setPaymentSuccess(true);
      toast({
        title: "Payment Successful",
        description: "Thank you! Your payment has been processed successfully."
      });

      fetchProjectData();
    } catch (error) {
      toast({
        title: "Payment Failed",
        description: error.response?.data?.detail || "Failed to process payment",
        variant: "destructive"
      });
    } finally {
      setProcessing(false);
    }
  };

  if (loading || !projectData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#e6007e] mx-auto"></div>
          <p className="mt-4 text-gray-600 font-body text-sm">Loading checkout...</p>
        </div>
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div className="text-center py-12">
        <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2 font-heading">Payment Successful!</h2>
        <p className="text-gray-600 font-body mb-4">
          Thank you for your payment. You'll receive a confirmation email shortly.
        </p>
        <button 
          onClick={onBack}
          className="px-6 py-2 bg-[#e6007e] hover:bg-[#c4006b] text-white rounded-lg font-medium font-body"
        >
          Back to Services
        </button>
      </div>
    );
  }

  const { project: projectDetails, contract } = projectData;
  // Fix VAT calculation to be ADDITIVE (not inclusive)
  const netAmount = projectDetails.total_amount; // Net amount is the project value
  const vatAmount = projectDetails.vat_enabled ? (netAmount * (projectDetails.vat_rate / 100)) : 0;
  const totalWithVat = netAmount + vatAmount; // VAT added on top

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Order Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 font-heading">Order Summary</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold text-gray-900 font-heading">{projectDetails.title}</h3>
            <p className="text-gray-600 text-sm font-body">
              {projectDetails.custom_scope || "Professional service delivery"}
            </p>
          </div>
          
          {/* Payment Instalments */}
          <div className="border-t border-gray-200 pt-4">
            <h4 className="font-medium text-gray-900 mb-3 font-heading">Payment Instalments</h4>
            <div className="space-y-2">
              {projectDetails.milestones.map((milestone, index) => (
                <div key={milestone.id} className="flex justify-between text-sm">
                  <span className="font-body">{milestone.title}</span>
                  <span className="font-medium font-body">£{milestone.amount.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* VAT Breakdown */}
          <div className="border-t border-gray-200 pt-4 space-y-2 text-sm">
            <div className="flex justify-between font-body">
              <span className="text-gray-600">Subtotal</span>
              <span>£{netAmount.toLocaleString()}</span>
            </div>
            <div className="flex justify-between font-body">
              <span className="text-gray-600">VAT ({projectDetails.vat_rate}%)</span>
              <span>£{vatAmount.toLocaleString()}</span>
            </div>
            <div className="border-t pt-2 flex justify-between text-base font-bold">
              <span className="font-heading">Total</span>
              <span className="font-heading">£{totalWithVat.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 font-heading">Payment</h2>
        
        {!contractSigned ? (
          /* Contract Signing */
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2 font-heading">Service Agreement Required</h3>
              <p className="text-sm text-blue-800 font-body">
                Please review and sign the service agreement before proceeding with payment.
              </p>
            </div>
            
            <div className="bg-white rounded-lg border shadow-sm p-4">
            {/* Service Agreement View Button */}
            <div className="bg-white rounded-lg border shadow-sm p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-gray-900 font-heading">Service Agreement</h4>
                  <p className="text-sm text-gray-600">Review the complete service contract before signing</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      // Show signed PDF if contract is signed, otherwise show preview
                      const cacheBuster = Date.now();
                      const contractUrl = projectData?.project?.contract_signed 
                        ? `${API}/contracts/${slug}/signed-pdf` 
                        : `${API}/contracts/${slug}/pdf?v=${cacheBuster}`;
                      window.open(contractUrl, '_blank', 'width=800,height=600');
                    }}
                    className="flex items-center space-x-2"
                  >
                    <FileText className="w-4 h-4" />
                    <span>{projectData?.project?.contract_signed ? 'View Signed Contract' : 'View Contract PDF'}</span>
                  </Button>
                  
                  {projectData?.project?.contract_signed && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = `${API}/contracts/${slug}/signed-pdf`;
                        link.download = `${projectData.project.title}_Signed_Contract.html`;
                        link.click();
                      }}
                      className="flex items-center space-x-2 bg-green-50 hover:bg-green-100 border-green-300"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      <span>Download Contract</span>
                    </Button>
                  )}
                </div>
              </div>
            </div>
            </div>
            
            <form onSubmit={handleSignContract} className="space-y-4">
              <div>
                <Label htmlFor="signature_name" className="font-body">Full Name (Electronic Signature)</Label>
                <Input
                  id="signature_name"
                  value={signatureData.full_name}
                  onChange={(e) => {
                    console.log('Name changed:', e.target.value);
                    setSignatureData({ ...signatureData, full_name: e.target.value });
                  }}
                  placeholder="Enter your full name"
                  className="font-body"
                  required
                />
              </div>
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="contract_agreed"
                  checked={signatureData.agreed}
                  onCheckedChange={(checked) => setSignatureData({ ...signatureData, agreed: checked })}
                />
                <Label htmlFor="contract_agreed" className="text-sm leading-4 font-body">
                  I agree to the terms and conditions and authorise electronic signature
                </Label>
              </div>
              <Button
                type="submit"
                disabled={processing || !signatureData.agreed || !signatureData.full_name.trim()}
                className="w-full bg-[#e6007e] hover:bg-[#c4006b] text-white font-body"
              >
                {processing ? "Signing..." : "Sign Agreement & Continue"}
              </Button>
            </form>
          </div>
        ) : (
          /* Payment Methods & Processing */
          <div className="space-y-6">
            <div className="flex items-center space-x-2 text-green-600 bg-green-50 p-3 rounded-lg">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm font-body">Contract signed - ready for payment</span>
            </div>
            
            {/* Payment Methods */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 font-heading">Payment Methods</h3>
              <div className="space-y-2">
                <button 
                  onClick={() => setSelectedPaymentMethod('apple_pay')}
                  className={`w-full p-3 border rounded-lg text-left transition-colors ${
                    selectedPaymentMethod === 'apple_pay' ? 'border-[#e6007e] bg-pink-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-black rounded flex items-center justify-center text-white text-xs font-bold">Pay</div>
                    <span className="font-medium text-gray-900 font-body">Apple Pay</span>
                  </div>
                </button>
                
                <button 
                  onClick={() => setSelectedPaymentMethod('card')}
                  className={`w-full p-3 border rounded-lg text-left transition-colors ${
                    selectedPaymentMethod === 'card' ? 'border-[#e6007e] bg-pink-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <CreditCard className="w-5 h-5 text-gray-600" />
                      <span className="font-medium text-gray-900 font-body">Card Payment</span>
                    </div>
                    <div className="flex space-x-1">
                      <div className="w-6 h-4 bg-blue-600 rounded text-white text-xs flex items-center justify-center font-bold">V</div>
                      <div className="w-6 h-4 bg-red-600 rounded text-white text-xs flex items-center justify-center font-bold">M</div>
                    </div>
                  </div>
                </button>
              </div>
            </div>
            
            {/* Payment Instalments */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 font-heading">Choose Payment</h3>
              <div className="space-y-2">
                {projectDetails.milestones.map((milestone, index) => {
                  const isPaid = milestone.status === 'paid';
                  const canPay = !isPaid && contractSigned && selectedPaymentMethod;
                  
                  return (
                    <div key={milestone.id} className={`border rounded-lg p-4 ${isPaid ? 'bg-green-50 border-green-200' : 'border-gray-200'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900 font-body">{milestone.title}</p>
                          <p className="text-sm text-gray-600 font-body">
                            {milestone.due_date ? `Due ${formatDate(milestone.due_date)}` : 'Due on agreement'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900 font-heading">£{milestone.amount.toLocaleString()}</p>
                          {isPaid ? (
                            <span className="text-sm text-green-600 font-body">Paid ✓</span>
                          ) : canPay ? (
                            <Button
                              onClick={() => handlePayment(milestone.id)}
                              disabled={processing}
                              size="sm"
                              className="bg-[#e6007e] hover:bg-[#c4006b] text-white font-body"
                            >
                              {processing ? "Processing..." : "Pay Now"}
                            </Button>
                          ) : (
                            <span className="text-sm text-gray-500 font-body">Select payment method</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Trust & Security */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-center space-x-6 text-xs text-gray-600 font-body">
                <div className="flex items-center space-x-1">
                  <Shield className="w-4 h-4 text-green-600" />
                  <span>SSL Secure</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Award className="w-4 h-4 text-blue-600" />
                  <span>Trust Badge</span>
                </div>
                <div className="flex items-center space-x-1">
                  <PiggyBank className="w-4 h-4 text-green-600" />
                  <span>VAT Vault Protected</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Full Contract Modal */}
      {showFullContract && (
        <Dialog open={showFullContract} onOpenChange={setShowFullContract}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Service Agreement - {project.title}</DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              <div 
                dangerouslySetInnerHTML={{ __html: projectData?.contract?.content || contract?.content || '' }}
                className="contract-preview"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {showFullContractModal && (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-[80vh] overflow-y-auto w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Service Agreement - {project.title}</h2>
                <button 
                  onClick={() => setShowFullContractModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              <div className="border rounded p-4">
                <div dangerouslySetInnerHTML={{ 
                  __html: contractContent || '<h1>SERVICE AGREEMENT</h1><p>Service agreement content loading...</p>' 
                }} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EmbedAllProjects = () => {
  const { userIdentifier } = useParams();
  const [businessData, setBusinessData] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accentColor, setAccentColor] = useState('#e6007e');

  useEffect(() => {
    fetchBusinessProjects();
    
    // Get accent color from URL params
    const urlParams = new URLSearchParams(window.location.search);
    const customAccent = urlParams.get('accent');
    if (customAccent) {
      const color = customAccent.startsWith('#') ? customAccent : `#${customAccent}`;
      setAccentColor(color);
    }
  }, [userIdentifier]);

  const fetchBusinessProjects = async () => {
    if (!userIdentifier || userIdentifier === 'demo-business') {
      // Show placeholder for demo or missing identifier
      setBusinessData(null);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/embed/all-projects/${userIdentifier}`);
      setBusinessData(response.data);
      if (response.data.business.brand_color) {
        setAccentColor(response.data.business.brand_color);
      }
    } catch (error) {
      console.error('Failed to load business projects:', error);
      setBusinessData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectSelect = (project) => {
    // Redirect directly to individual project checkout with 5 payment options
    window.open(project.hosted_url, '_top');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-current mx-auto" style={{ color: accentColor }}></div>
          <p className="mt-4 text-gray-600 font-body text-sm">Loading services...</p>
        </div>
      </div>
    );
  }

  // Show placeholder when no valid business identifier
  if (!businessData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow-sm p-8 border-2 border-dashed border-gray-300">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2 font-heading">Please use your payment link</h2>
            <p className="text-gray-600 font-body mb-4">
              This payment page requires a specific link to access your services.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800 font-body">
                <strong>For business owners:</strong> Share your specific payment links with clients, or use your personalized embed code.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { business, projects } = businessData;

  return (
    <div className="min-h-screen bg-gray-50 p-4" style={{ 
      '--accent': accentColor,
      '--accent-hover': `${accentColor}dd`
    }}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border border-gray-200">
          <div className="flex items-center space-x-4">
            {business.logo_url && (
              <img 
                src={business.logo_url} 
                alt={business.name}
                className="w-12 h-12 object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <div>
              <h1 className="text-2xl font-bold text-gray-900 font-heading">
                {business.name}
              </h1>
              <p className="text-gray-600 font-body">Choose a service to get started</p>
            </div>
          </div>
        </div>

        {/* Services Grid */}
        {projects.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center border border-gray-200">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2 font-heading">No Services Available</h2>
            <p className="text-gray-600 font-body">
              This business doesn't have any active services at the moment.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {projects.map((project) => (
              <div 
                key={project.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md transition-all hover:border-gray-300"
                onClick={() => handleProjectSelect(project)}
                style={{
                  borderColor: accentColor + '20'
                }}
              >
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 font-heading">
                      {project.title}
                    </h3>
                    <ArrowRight className="w-5 h-5 text-gray-400" />
                  </div>
                  <p className="text-gray-600 text-sm font-body line-clamp-2">
                    {project.description || "Professional service delivery with structured payment options"}
                  </p>
                </div>
                
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-xl font-bold text-gray-900 font-heading">
                      £{project.total_amount.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-500 font-body">
                      {getPaymentStructureLabel(project.payment_structure)}
                    </div>
                  </div>
                  <button 
                    className="px-4 py-2 rounded-lg text-white font-medium font-body text-sm transition-colors hover:opacity-90"
                    style={{ backgroundColor: accentColor }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleProjectSelect(project);
                    }}
                  >
                    Get Quote
                  </button>
                </div>
                
                {/* Status indicator */}
                <div className="pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500 font-body">
                      {project.contract_agreed ? 'Ready for payment' : 'Contract pending'}
                    </span>
                    <div className={`w-2 h-2 rounded-full ${
                      project.contract_agreed ? 'bg-green-500' : 'bg-yellow-500'
                    }`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Footer */}
        <div className="text-center mt-6 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-center space-x-2 text-gray-500">
            <Shield className="w-4 h-4" />
            <span className="text-sm font-body">Secured by Paify</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const DirectEmbeddedCheckout = ({ project, business }) => {
  const [projectData, setProjectData] = useState(null);
  const [contractSigned, setContractSigned] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [signatureData, setSignatureData] = useState({
    full_name: '',
    email: '',
    position: '',
    company: '',
    agreed: false,
    identity_confirmed: false
  });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (project) {
      fetchProjectData();
    }
  }, [project]);
  // Update signature data when project becomes available
  useEffect(() => {
    if (project) {
      setSignatureData(prev => ({
        ...prev,
        full_name: project.client_name || '',
        email: project.client_email || '',
        company: project.client_company || ''
      }));
    }
  }, [project]);

  const fetchProjectData = async () => {
    try {
      const response = await axios.get(`${API}/checkout/${project.slug}`);
      setProjectData(response.data);
      setContractSigned(response.data.project.contract_agreed);
    } catch (error) {
      console.error('Failed to load project data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignContract = async (e) => {
    e.preventDefault();
    if (!signatureData.agreed || !signatureData.full_name.trim()) {
      toast({
        title: "Agreement Required",
        description: "Please enter your full name and agree to contract terms"
      });
      return;
    }

    setProcessing(true);
    try {
      await axios.post(`${API}/checkout/${project.slug}/sign`, {
        full_name: signatureData.full_name.trim(),
        agreed: signatureData.agreed,
        ip_address: '127.0.0.1',
        user_agent: navigator.userAgent
      });

      setContractSigned(true);
      fetchProjectData();
    } catch (error) {
      console.log('🔥 API CALL FAILED:', error);
      console.log('🔥 ERROR RESPONSE:', error.response?.data);
      toast({
        title: "Signing Failed",
        description: error.response?.data?.detail || "Please try again",
        variant: "destructive"
      });
      setProcessing(false);
    }
  };

  const handlePayment = async (milestoneId) => {
    if (!selectedPaymentMethod) {
      toast({
        title: "Select Payment Method",
        description: "Please choose how you'd like to pay"
      });
      return;
    }

    setProcessing(true);
    try {
      await axios.post(`${API}/checkout/${project.slug}/pay/${milestoneId}`);
      setPaymentSuccess(true);
      fetchProjectData();
    } catch (error) {
      console.error('Payment failed:', error);
    } finally {
      setProcessing(false);
    }
  };

  if (loading || !projectData) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#e6007e] mx-auto"></div>
          <p className="mt-4 text-gray-600 font-body">Loading secure checkout...</p>
        </div>
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
        <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2 font-heading">Payment Successful!</h2>
        <p className="text-gray-600 font-body mb-4">
          Thank you! Your payment has been processed. You'll receive confirmation shortly.
        </p>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-w-md mx-auto">
          <p className="text-sm text-green-800 font-body">
            <strong>Receipt sent</strong> to your email
          </p>
        </div>
      </div>
    );
  }

  const { project: projectDetails, contract } = projectData;
  // Fix VAT calculation to be ADDITIVE (not inclusive)
  const netAmount = projectDetails.total_amount; // Net amount is the project value
  const vatAmount = projectDetails.vat_enabled ? (netAmount * (projectDetails.vat_rate / 100)) : 0;
  const totalWithVat = netAmount + vatAmount; // VAT added on top

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* Order Summary - Left Side */}
      <div className="lg:col-span-3 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6 font-heading">Order Summary</h2>
        
        {/* Service Details */}
        <div className="border-b border-gray-200 pb-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2 font-heading">{projectDetails.title}</h3>
          <p className="text-gray-600 text-sm font-body mb-4">
            {projectDetails.custom_scope || "Professional service delivery with structured payments"}
          </p>
          <div className="flex items-center text-sm text-gray-500 font-body">
            <span>Service by {business.name}</span>
          </div>
        </div>

        {/* Payment Instalments */}
        <div className="border-b border-gray-200 pb-6 mb-6">
          <h4 className="text-base font-semibold text-gray-900 mb-4 font-heading">Payment Instalments</h4>
          <div className="space-y-3">
            {projectDetails.milestones.map((milestone, index) => {
              const isPaid = milestone.status === 'paid';
              
              return (
                <div 
                  key={milestone.id}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    isPaid ? 'bg-green-50 border border-green-200' : 'border border-gray-200'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${isPaid ? 'bg-green-600' : 'bg-gray-300'}`} />
                    <div>
                      <p className="font-medium text-gray-900 font-body">{milestone.title}</p>
                      <p className="text-sm text-gray-500 font-body">
                        {milestone.due_date ? `Due ${formatDate(milestone.due_date)}` : 'Due on agreement'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900 font-heading">£{milestone.amount.toLocaleString()}</p>
                    {isPaid && <p className="text-sm text-green-600 font-body">Paid ✓</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* VAT Breakdown */}
        <div className="space-y-2 text-sm">
          <div className="flex justify-between font-body">
            <span className="text-gray-600">Subtotal</span>
            <span>£{netAmount.toLocaleString()}</span>
          </div>
          <div className="flex justify-between font-body">
            <span className="text-gray-600">VAT ({projectDetails.vat_rate}%)</span>
            <span>£{vatAmount.toLocaleString()}</span>
          </div>
          <div className="border-t pt-2 flex justify-between text-lg font-bold">
            <span className="font-heading">Total</span>
            <span className="font-heading">£{totalWithVat.toLocaleString()}</span>
          </div>
        </div>

        {/* Trust Features */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="text-xs text-gray-600">
              <Shield className="w-4 h-4 mx-auto mb-1 text-green-600" />
              <p className="font-body">SSL Secure</p>
            </div>
            <div className="text-xs text-gray-600">
              <PiggyBank className="w-4 h-4 mx-auto mb-1 text-green-600" />
              <p className="font-body">VAT Vault</p>
            </div>
            <div className="text-xs text-gray-600">
              <Award className="w-4 h-4 mx-auto mb-1 text-blue-600" />
              <p className="font-body">Trust Badge</p>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Panel - Right Side */}
      <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-6 font-heading">Secure Payment</h2>

        {!contractSigned ? (
          /* Contract Agreement */
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center space-x-2 mb-2">
                <FileText className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-semibold text-blue-900 font-body">Service Agreement</span>
              </div>
              <p className="text-xs text-blue-800 font-body">
                Review and sign the service agreement to proceed with payment.
              </p>
            </div>
            
            {/* Service Agreement View Button */}
            <div className="bg-white rounded-lg border shadow-sm p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-gray-900 font-heading">Service Agreement</h4>
                  <p className="text-sm text-gray-600">Review the complete service contract before signing</p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    // Show signed PDF if contract is signed, otherwise show preview
                    const cacheBuster = Date.now();
                    const contractUrl = projectData?.project?.contract_signed 
                      ? `${API}/contracts/${slug}/signed-pdf` 
                      : `${API}/contracts/${slug}/pdf?v=${cacheBuster}`;
                    window.open(contractUrl, '_blank', 'width=800,height=600');
                  }}
                  className="flex items-center space-x-2"
                >
                  <FileText className="w-4 h-4" />
                  <span>{projectData?.project?.contract_signed ? 'View Signed Contract' : 'View Contract PDF'}</span>
                </Button>
              </div>
            </div>
            
            <form onSubmit={handleSignContract} className="space-y-4">
              <div>
                <Label className="text-sm font-body">Full Name (Electronic Signature)</Label>
                <Input
                  value={signatureData.full_name}
                  onChange={(e) => {
                    console.log('Name changed:', e.target.value);
                    setSignatureData({ ...signatureData, full_name: e.target.value });
                  }}
                  placeholder="Enter your full name"
                  className="font-body"
                  required
                />
              </div>
              <div className="flex items-start space-x-2">
                <Checkbox
                  checked={signatureData.agreed}
                  onCheckedChange={(checked) => setSignatureData({ ...signatureData, agreed: checked })}
                />
                <Label className="text-xs leading-4 font-body">
                  I agree to the service agreement terms and authorize electronic signature
                </Label>
              </div>
              <Button
                type="submit"
                disabled={processing || !signatureData.agreed}
                className="w-full bg-[#e6007e] hover:bg-[#c4006b] text-white font-body"
              >
                {processing ? "Signing..." : "Sign & Continue to Payment"}
              </Button>
            </form>
          </div>
        ) : (
          /* Payment Interface */
          <div className="space-y-6">
            {/* Contract Signed Confirmation */}
            <div className="flex items-center space-x-2 text-green-600 bg-green-50 p-3 rounded-lg">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm font-body">Agreement signed ✓</span>
            </div>
            
            {/* Payment Methods */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 font-heading">Payment Method</h3>
              <div className="space-y-2">
                <button 
                  onClick={() => setSelectedPaymentMethod('apple_pay')}
                  className={`w-full p-3 border rounded-lg text-left transition-colors ${
                    selectedPaymentMethod === 'apple_pay' ? 'border-[#e6007e] bg-pink-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-black rounded flex items-center justify-center text-white text-xs font-bold">Pay</div>
                    <span className="font-medium text-gray-900 font-body">Apple Pay</span>
                  </div>
                </button>
                
                <button 
                  onClick={() => setSelectedPaymentMethod('card')}
                  className={`w-full p-3 border rounded-lg text-left transition-colors ${
                    selectedPaymentMethod === 'card' ? 'border-[#e6007e] bg-pink-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <CreditCard className="w-5 h-5 text-gray-600" />
                      <span className="font-medium text-gray-900 font-body">Card</span>
                    </div>
                    <div className="flex space-x-1">
                      <div className="w-6 h-4 bg-blue-600 rounded text-white text-xs flex items-center justify-center font-bold">V</div>
                      <div className="w-6 h-4 bg-red-600 rounded text-white text-xs flex items-center justify-center font-bold">M</div>
                    </div>
                  </div>
                </button>

              </div>
            </div>

            {/* Payment Options */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 font-heading">Choose Payment</h3>
              <div className="space-y-2">
                {projectDetails.milestones.map((milestone) => {
                  const isPaid = milestone.status === 'paid';
                  const canPay = !isPaid && contractSigned && selectedPaymentMethod;
                  
                  return (
                    <div key={milestone.id} className={`border rounded-lg p-4 ${isPaid ? 'bg-green-50 border-green-200' : ''}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900 font-body">{milestone.title}</p>
                          <p className="text-sm text-gray-600 font-body">
                            {milestone.due_date ? formatDate(milestone.due_date) : 'Due on agreement'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900 font-heading">£{milestone.amount.toLocaleString()}</p>
                          {isPaid && <span className="text-sm text-green-600 font-body">Paid ✓</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Main Pay Button */}
            <div className="space-y-4">
              {/* Pay Next Milestone Button */}
              {(() => {
                const nextUnpaidMilestone = projectDetails.milestones.find(m => m.status !== 'paid');
                const canPay = contractSigned && selectedPaymentMethod && nextUnpaidMilestone;
                
                return (
                  <Button
                    onClick={() => canPay && handlePayment(nextUnpaidMilestone.id)}
                    disabled={!canPay || processing}
                    className="w-full h-12 bg-[#e6007e] hover:bg-[#c4006b] text-white font-semibold font-body"
                  >
                    {processing ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Processing payment...
                      </>
                    ) : !contractSigned ? (
                      "Sign agreement to continue"
                    ) : !selectedPaymentMethod ? (
                      "Select payment method above"
                    ) : !nextUnpaidMilestone ? (
                      "All payments completed"
                    ) : (
                      <>
                        <Shield className="w-4 h-4 mr-2" />
                        Pay securely £{nextUnpaidMilestone.amount.toLocaleString()}
                      </>
                    )}
                  </Button>
                );
              })()}

              {/* Pay All Remaining Option */}
              {(() => {
                const unpaidMilestones = projectDetails.milestones.filter(m => m.status !== 'paid');
                const totalRemaining = unpaidMilestones.reduce((sum, m) => sum + m.amount, 0);
                const canPayAll = contractSigned && selectedPaymentMethod && unpaidMilestones.length > 1;
                
                return canPayAll ? (
                  <Button
                    onClick={async () => {
                      setProcessing(true);
                      try {
                        for (const milestone of unpaidMilestones) {
                          await handlePayment(milestone.id);
                        }
                      } finally {
                        setProcessing(false);
                      }
                    }}
                    disabled={processing}
                    variant="outline"
                    className="w-full h-12 border-[#e6007e] text-[#e6007e] hover:bg-[#e6007e] hover:text-white font-semibold font-body"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Pay all remaining £{totalRemaining.toLocaleString()}
                  </Button>
                ) : null;
              })()}
            </div>

            {/* Payment Security */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-center space-x-6 text-xs text-gray-600 font-body">
                <div className="flex items-center space-x-1">
                  <Shield className="w-3 h-3 text-green-600" />
                  <span>256-bit SSL</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Award className="w-3 h-3 text-blue-600" />
                  <span>Verified</span>
                </div>
                <div className="flex items-center space-x-1">
                  <PiggyBank className="w-3 h-3 text-green-600" />
                  <span>VAT Protected</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      
      {showFullContractModal && (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-[80vh] overflow-y-auto w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Service Agreement - {project.title}</h2>
                <button 
                  onClick={() => setShowFullContractModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              <div className="border rounded p-4">
                <div dangerouslySetInnerHTML={{ 
                  __html: contractContent || project?.contract?.content || '<h1>SERVICE AGREEMENT</h1><p>Loading service agreement...</p>' 
                }} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Embedded Checkout With Choice Component (Step 3 of Embed Flow)

const EmbeddedCheckoutWithChoice = ({ project, projectSlug, business, selectedPaymentOption, isPaymentSaving, onBack, onContractSigned }) => {
  const [contractSigned, setContractSigned] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [signatureData, setSignatureData] = useState({
    full_name: '',
    email: '',
    position: '',
    company: '',
    agreed: false,
    identity_confirmed: false
  });
  const [processing, setProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const { toast } = useToast();

  // Check project status on load
  useEffect(() => {
    fetchProjectData();
  }, [project]);
  // Update signature data when project becomes available
  useEffect(() => {
    if (project) {
      setSignatureData(prev => ({
        ...prev,
        full_name: project.client_name || '',
        email: project.client_email || '',
        company: project.client_company || ''
      }));
    }
  }, [project]);

  const fetchProjectData = async () => {
    try {
      const response = await axios.get(`${API}/checkout/${projectSlug}`);
      setProjectData(response.data);
      
      // Only set state flags if they haven't been set by contract signing
      if (!contractSigned && !showPaymentForm) {
        // If project is already active with milestones, show payment interface
        if (response.data.project.status === 'active' && response.data.project.milestones?.length > 0) {
          setContractSigned(true);
          setShowPaymentForm(true);
        }
        // If project has contract_agreed set to true, also show payment
        else if (response.data.project.contract_agreed === true) {
          setContractSigned(true);
          setShowPaymentForm(true);
        }
      }
    } catch (error) {
      console.error('Failed to fetch project data:', error);
    }
  };

  const handleSignContract = async (e) => {
    e.preventDefault();
    if (!signatureData.agreed || !signatureData.full_name.trim()) {
      toast({
        title: "Signature Required",
        description: "Please enter your full name and agree to the contract"
      });
      return;
    }

    setProcessing(true);
    try {
      // Sign contract with chosen payment structure
      await axios.post(`${API}/checkout/${projectSlug}/choose-payment`, {
        payment_structure: selectedPaymentOption.id,
        full_name: signatureData.full_name.trim(),
        agreed: signatureData.agreed,
        ip_address: '127.0.0.1',
        user_agent: navigator.userAgent
      });

      toast({
        title: "Contract Signed",
        description: "Loading payment options..."
      });

      // Set local state FIRST to ensure payment interface shows
      setContractSigned(true);
      setShowPaymentForm(true);
      
      // Then fetch updated project data (without overriding local state)
      await fetchProjectData();
      
      // Notify parent component that contract is signed
      if (onContractSigned) {
        onContractSigned();
      }
      
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to sign contract",
        variant: "destructive"
      });
    } finally {
      setProcessing(false);
    }
  };

  const handlePayment = async (paymentId) => {
    if (!selectedPaymentMethod) {
      toast({
        title: "Payment Method Required",
        description: "Please select a payment method"
      });
      return;
    }

    setProcessing(true);
    try {
      // For client choice projects, we need to get the updated project with milestones
      const updatedResponse = await axios.get(`${API}/checkout/${project.slug}`);
      const updatedProject = updatedResponse.data.project;
      
      // Find the first unpaid milestone
      const firstMilestone = updatedProject.milestones?.find(m => m.status !== 'paid');
      
      if (firstMilestone) {
        await axios.post(`${API}/checkout/${project.slug}/pay/${firstMilestone.id}`);
        setPaymentSuccess(true);
        toast({
          title: "Payment Successful",
          description: "Thank you! Your payment has been processed successfully."
        });
      } else {
        toast({
          title: "No Payment Required",
          description: "This project doesn't require immediate payment."
        });
      }
    } catch (error) {
      toast({
        title: "Payment Failed",
        description: "Failed to process payment. Please try again.",
        variant: "destructive"
      });
    } finally {
      setProcessing(false);
    }
  };

  // After successful payment
  if (paymentSuccess) {
    return (
      <div className="text-center py-12">
        <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-4 font-heading">Payment Successful!</h2>
        <p className="text-gray-600 font-body mb-4">
          Thank you! Your payment has been processed successfully.
        </p>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-w-md mx-auto">
          <p className="text-sm text-green-800 font-body">
            <strong>Receipt sent</strong> to your email. Work begins immediately!
          </p>
        </div>
      </div>
    );
  }

  // Contract signed - show payment interface
  if (contractSigned && showPaymentForm) {
    const currentProject = projectData?.project || project;
    const milestones = projectData?.project?.milestones || [];
    const firstUnpaidMilestone = milestones.find(m => m.status === 'pending');

    return (
      <div className="max-w-4xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Payment Summary */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 font-heading">Order Summary</h3>
            
            <div className="space-y-3 mb-4">
              <div className="flex justify-between text-sm">
                <span className="font-body">{currentProject.title}</span>
                <span className="font-medium font-body">£{currentProject.total_amount.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-body">Payment Structure</span>
                <span className="font-medium font-body">{selectedPaymentOption.title}</span>
              </div>
              {firstUnpaidMilestone && (
                <div className="flex justify-between text-sm">
                  <span className="font-body">Current Payment</span>
                  <span className="font-medium font-body">£{firstUnpaidMilestone.amount.toLocaleString()}</span>
                </div>
              )}
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
              <div className="flex items-center space-x-2 text-green-600">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-body">Contract signed ✓</span>
              </div>
            </div>

            {/* Payment Instalments */}
            {milestones.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-semibold text-gray-900 font-heading">Payment Schedule</h4>
                {milestones.map((milestone, index) => (
                  <div key={milestone.id} className="flex justify-between text-sm">
                    <span className="font-body">
                      {index + 1}. {milestone.title}
                    </span>
                    <span className="font-medium font-body">
                      £{milestone.amount.toLocaleString()}
                      {milestone.status === 'paid' && <span className="text-green-600 ml-1">✓</span>}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Payment Methods & Processing */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 font-heading">Payment Method</h3>
            
            {/* Payment Methods */}
            <div className="space-y-3 mb-6">
              <button 
                onClick={() => setSelectedPaymentMethod('apple_pay')}
                className={`w-full p-3 border rounded-lg text-left transition-colors ${
                  selectedPaymentMethod === 'apple_pay' ? 'border-[#e6007e] bg-pink-50' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-black rounded flex items-center justify-center text-white text-xs font-bold">Pay</div>
                  <span className="font-medium text-gray-900 font-body">Apple Pay</span>
                </div>
              </button>
              
              <button 
                onClick={() => setSelectedPaymentMethod('card')}
                className={`w-full p-3 border rounded-lg text-left transition-colors ${
                  selectedPaymentMethod === 'card' ? 'border-[#e6007e] bg-pink-50' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <CreditCard className="w-5 h-5 text-gray-600" />
                    <span className="font-medium text-gray-900 font-body">Card Payment</span>
                  </div>
                  <div className="flex space-x-1">
                    <div className="w-6 h-4 bg-blue-600 rounded text-white text-xs flex items-center justify-center font-bold">V</div>
                    <div className="w-6 h-4 bg-red-600 rounded text-white text-xs flex items-center justify-center font-bold">M</div>
                  </div>
                </div>
              </button>

            </div>

            {/* Payment Processing */}
            <div className="space-y-4">
              {/* Calculate payment amount based on selected option */}
              {(() => {
                let paymentAmount = 0;
                let paymentDescription = '';
                
                if (selectedPaymentOption.id === 'full_upfront') {
                  paymentAmount = project.total_amount;
                  paymentDescription = 'Pay full amount now';
                } else if (selectedPaymentOption.id === 'fifty_fifty') {
                  paymentAmount = calculateVATInclusiveAmount(project.total_amount * 0.5, project.vat_rate, project.vat_enabled);
                  paymentDescription = 'Pay first 50% now';
                } else if (selectedPaymentOption.id === 'three_month_milestones') {
                  paymentAmount = project.total_amount * 0.4;
                  paymentDescription = 'Pay first instalment (40%) now';
                } else if (selectedPaymentOption.id === 'bnpl') {
                  return (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-800 font-body">
                        <strong>BNPL Selected:</strong> No payment required now. 
                        Work will begin and payment arranged through approved provider.
                      </p>
                      <Button
                        onClick={() => setPaymentSuccess(true)}
                        className="w-full mt-3 bg-[#e6007e] hover:bg-[#c4006b] text-white font-body"
                      >
                        Confirm BNPL & Start Work
                      </Button>
                    </div>
                  );
                }

                return (
                  <Button
                    onClick={() => {
                      // Process payment for the chosen structure
                      handlePayment('first_payment');
                    }}
                    disabled={!selectedPaymentMethod || processing}
                    className="w-full h-12 bg-[#e6007e] hover:bg-[#c4006b] text-white font-semibold font-body"
                  >
                    {processing ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Processing payment...
                      </>
                    ) : (
                      <>
                        <Shield className="w-4 h-4 mr-2" />
                        {paymentDescription} - £{paymentAmount.toLocaleString()}
                      </>
                    )}
                  </Button>
                );
              })()}
            </div>

            {/* Trust Features */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-center space-x-6 text-xs text-gray-500 font-body">
                <div className="flex items-center space-x-1">
                  <Shield className="w-3 h-3 text-green-600" />
                  <span>256-bit SSL</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Award className="w-3 h-3 text-blue-600" />
                  <span>PCI Compliant</span>
                </div>
                <div className="flex items-center space-x-1">
                  <PiggyBank className="w-3 h-3 text-green-600" />
                  <span>VAT Vault</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900 font-heading">
          Review & Sign: {selectedPaymentOption.title}
        </h2>
        <button 
          onClick={onBack}
          className="text-gray-600 hover:text-gray-900 font-body"
        >
          ← Change Payment
        </button>
      </div>

      {/* Payment Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-blue-900 mb-2 font-heading">{project.title}</h3>
        <p className="text-sm text-blue-800 font-body mb-3">
          Payment Structure: <strong>{selectedPaymentOption.title}</strong>
        </p>
        <p className="text-sm text-blue-700 font-body mb-3">
          {selectedPaymentOption.description}
        </p>
        <div className="text-lg font-bold text-blue-900 font-heading">
          Total: £{project.total_amount.toLocaleString()}
        </div>
      </div>

      {/* Contract Terms Preview */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h4 className="font-semibold text-gray-900 mb-3 font-heading">Service Agreement Terms</h4>
        <div className="text-sm text-gray-700 font-body leading-relaxed">
          <p><strong>Service:</strong> {project.title}</p>
          <p><strong>Client:</strong> {project.client_name}</p>
          <p><strong>Total Amount:</strong> £{project.total_amount.toLocaleString()}</p>
          <p><strong>Payment Structure:</strong> {selectedPaymentOption.title}</p>
          <br />
          <p><strong>Payment Terms:</strong></p>
          <p>{selectedPaymentOption.description}</p>
        </div>
      </div>

      {/* Electronic Signature */}
      <form onSubmit={handleSignContract} className="space-y-4">
        <div>
          <Label className="font-body">Full Name (Electronic Signature)</Label>
          <Input
            value={signatureData.full_name}
            onChange={(e) => {
              console.log('Name changed:', e.target.value);
              setSignatureData({ ...signatureData, full_name: e.target.value });
            }}
            placeholder="Enter your full name"
            className="font-body"
            required
          />
        </div>
        
        <div className="flex items-start space-x-2">
          <Checkbox
            id="embed_agreement_checkbox"
            checked={signatureData.agreed}
            onCheckedChange={(checked) => setSignatureData({ ...signatureData, agreed: checked })}
          />
          <Label htmlFor="embed_agreement_checkbox" className="text-sm leading-5 font-body cursor-pointer">
            I agree to the service agreement with <strong>{selectedPaymentOption.title}</strong> payment structure and authorize work to begin
          </Label>
        </div>
        
        <Button
          type="submit"
          disabled={processing || !signatureData.agreed || !signatureData.full_name.trim()}
          className="w-full bg-[#e6007e] hover:bg-[#c4006b] text-white font-semibold font-body h-12"
        >
          {processing ? "Processing..." : `Confirm ${selectedPaymentOption.title} & Start Work`}
        </Button>
      </form>

      {/* Trust Footer */}
      <div className="mt-6 pt-6 border-t border-gray-200 text-center">
        <div className="flex items-center justify-center space-x-4 text-xs text-gray-500 font-body">
          <div className="flex items-center space-x-1">
            <Shield className="w-3 h-3 text-green-600" />
            <span>SSL Secure</span>
          </div>
          <div className="flex items-center space-x-1">
            <Award className="w-3 h-3 text-blue-600" />
            <span>Trust Badge</span>
          </div>
          <div className="flex items-center space-x-1">
            <PiggyBank className="w-3 h-3 text-green-600" />
            <span>VAT Vault</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Logo Upload Component for Subscribers

// Settings, Privacy, Terms, Help pages extracted to ./components/

// Embeddable Checkout Component

const EmbeddableCheckout = () => {
  const { slug } = useParams();
  const [accentColor, setAccentColor] = useState('#e6007e');
  const [projectData, setProjectData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const customAccent = urlParams.get('accent');
    if (customAccent && customAccent.startsWith('%23')) {
      setAccentColor('#' + customAccent.slice(3));
    } else if (customAccent && customAccent.startsWith('#')) {
      setAccentColor(customAccent);
    }
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--embed-accent-color', accentColor);
    root.style.setProperty('--embed-accent-hover', adjustBrightness(accentColor, -20));
  }, [accentColor]);

  useEffect(() => {
    fetchProjectData();
  }, [slug]);

  const fetchProjectData = async () => {
    try {
      const timestamp = Date.now();
      const response = await axios.get(`${API}/checkout/${slug}?t=${timestamp}`);
      setProjectData(response.data);
    } catch (error) {
      console.error('Failed to load checkout:', error);
      setProjectData(null);
    } finally {
      setLoading(false);
    }
  };

  const adjustBrightness = (hex, percent) => {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = (num >> 8 & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e6007e] mx-auto"></div>
          <p className="mt-4 text-gray-600 font-body">Loading...</p>
        </div>
      </div>
    );
  }

  if (!projectData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2 font-heading">Payment Page Not Found</h1>
            <p className="text-gray-600 font-body mb-4">This payment page is not available.</p>
          </div>
        </div>
      </div>
    );
  }

  const { project, provider } = projectData;

  if (project.status === 'pending_client_choice') {
    return (
      <div className="embed-checkout min-h-screen bg-gray-50" style={{ '--primary': accentColor, '--primary-hover': adjustBrightness(accentColor, -20) }}>
        <div className="p-4">
          <ClientPaymentChoice project={project} provider={provider} onChoiceComplete={() => window.location.reload()} onRefreshData={fetchProjectData} />
        </div>
        <Toaster />
      </div>
    );
  }

  return (
    <div className="embed-checkout min-h-screen bg-gray-50 p-4" style={{ '--primary': accentColor, '--primary-hover': adjustBrightness(accentColor, -20) }}>
      <CheckoutPage />
    </div>
  );
};

// Subscription Success Page Component

export { EmbedBusiness, EmbeddedProjectCheckout, EmbedAllProjects, DirectEmbeddedCheckout, EmbeddedCheckoutWithChoice, EmbeddableCheckout };
